import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import GroupedColumnPickerPanel, { ColumnPickerGroup, useCloseOnOutsideClick } from './GroupedColumnPickerPanel';
import KpiInfoTooltip from './KpiInfoTooltip';
import TablePager from './TablePager';
import { CrewingHorizontalScrollView, CrewingVerticalScrollView } from './webScrollStyles';
import type { QualityMetricsTotals } from '../crewingAnalyticsApi';

export interface ExecutionQualityMetricsRow {
  region: string;
  market: string;
  location: string;
  year: string;
  period: string;
  week: string;
  category: string;
  zone: string;
  forecastedHours: number;
  scheduledHours: number;
  estimatedHours: number;
  // Null when this row has no final Kronos actual yet.
  actualHours: number | null;
  crewingAccuracy: number | null;
  crewingEfficiency: number | null;
  crewingCompliance: number;
}

interface ExecutionQualityMetricsTableProps {
  title: string;
  note?: string;
  rows: ExecutionQualityMetricsRow[];
  selectedColumnKeys?: K[];
  /** Called with the newly-selected dimension keys so the caller can re-fetch server-side grouped rows. */
  onColumnsChange?: (dimensionKeys: string[]) => void;
  /** Aggregate metrics across the FULL filtered result set (backend `totals`) — render this
   * directly for the grand-total row, never recompute it from the current page's `rows`. */
  backendTotals?: QualityMetricsTotals | null;
  /** Full filtered grouped-row count (backend `total`), used by the pager and footnote. */
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  pageLoading?: boolean;
  /** Selectable rows-per-page values — the picker is hidden if this or `onPageSizeChange` is omitted. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  /** Server-side sort state (backend `sortBy`/`sortDirection`) — rows arrive pre-sorted, so this
   * table no longer sorts client-side. `sortBy` is null/undefined for the default grouping order. */
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc';
  /** Called with the clicked column's key — caller re-fetches page 0 with the new sort applied. */
  onSortChange?: (columnKey: string) => void;
}

type K = keyof ExecutionQualityMetricsRow;

interface C {
  key: K;
  label: string;
  width: 'wide' | 'std' | 'num';
  numeric?: boolean;
  percent?: boolean;
  link?: boolean;
  /** Hover tooltip naming the rate's numerator/denominator. */
  tooltip?: string;
}

const WIDE = 122;
const STD = 64;
const NUM = 108;

const COLS: C[] = [
  { key: 'region', label: 'Region / Unit', width: 'wide' },
  { key: 'market', label: 'Market / POD', width: 'wide' },
  { key: 'location', label: 'Location', width: 'wide', link: true },
  { key: 'year', label: 'Year', width: 'std' },
  { key: 'period', label: 'Period', width: 'std' },
  { key: 'week', label: 'Week', width: 'std' },
  { key: 'category', label: 'Category', width: 'std' },
  { key: 'zone', label: 'Zone', width: 'std' },
  { key: 'forecastedHours', label: 'Forecasted Hours', width: 'num', numeric: true },
  { key: 'scheduledHours', label: 'Scheduled Hours', width: 'num', numeric: true },
  { key: 'estimatedHours', label: 'Estimated Hours', width: 'num', numeric: true },
  { key: 'actualHours', label: 'Actual (Kronos) Hours', width: 'num', numeric: true },
  {
    key: 'crewingAccuracy', label: 'Crewing Accuracy %', width: 'num', numeric: true, percent: true,
    tooltip: 'Scheduled Hours / Actual (Kronos) Hours, computed from the summed hours in this group. Capped at 100; overage mirrors back.',
  },
  {
    key: 'crewingEfficiency', label: 'Crewing Efficiency %', width: 'num', numeric: true, percent: true,
    tooltip: 'Estimated Hours / Actual (Kronos) Hours, computed from the summed hours in this group. Capped at 100.',
  },
  {
    key: 'crewingCompliance', label: 'Crewing Compliance %', width: 'num', numeric: true, percent: true,
    tooltip: 'Forecasted Hours / Scheduled Hours, computed from the summed hours in this group. Capped at 100.',
  },
];

const n = (v: number) => v.toLocaleString();

// Only these (groupable) columns are toggleable in the picker — metrics always display.
const DIMENSION_KEYS: K[] = ['region', 'market', 'location', 'year', 'period', 'week', 'category', 'zone'];
const METRIC_DEFS = COLS.filter((c) => !DIMENSION_KEYS.includes(c.key));

const COLUMN_GROUPS: ColumnPickerGroup[] = [
  {
    key: 'location-hierarchy',
    label: 'Location Hierarchy',
    options: [
      { key: 'region', label: 'Region / Unit' },
      { key: 'market', label: 'Market / POD' },
      { key: 'location', label: 'Location' },
    ],
  },
  {
    key: 'timeframe',
    label: 'Timeframe',
    options: [
      { key: 'year', label: 'Year' },
      { key: 'period', label: 'Period' },
      { key: 'week', label: 'Week' },
    ],
  },
  {
    key: 'other-attributes',
    label: 'Other Attributes',
    options: [
      { key: 'category', label: 'Category' },
      { key: 'zone', label: 'Zone' },
    ],
  },
];

const ExecutionQualityMetricsTable: React.FC<ExecutionQualityMetricsTableProps> = ({ title, note, rows, selectedColumnKeys: controlledSelectedColumnKeys, onColumnsChange, backendTotals, total, page = 0, pageSize = 25, onPageChange, pageLoading, pageSizeOptions, onPageSizeChange, sortBy, sortDirection = 'asc', onSortChange }) => {
  const [open, setOpen] = useState(false);
  const columnPickerWrapRef = useRef<View>(null);
  useCloseOnOutsideClick(columnPickerWrapRef, () => setOpen(false), open);
  const [selected, setSelected] = useState<K[]>(controlledSelectedColumnKeys ?? DIMENSION_KEYS);

  useEffect(() => {
    if (controlledSelectedColumnKeys) {
      setSelected(controlledSelectedColumnKeys);
    }
  }, [controlledSelectedColumnKeys]);

  const handleColumnsChange = (keys: K[]) => {
    setSelected(keys);
    onColumnsChange?.(keys);
  };
  const cols = useMemo(
    () => [...COLS.filter((c) => DIMENSION_KEYS.includes(c.key) && selected.includes(c.key)), ...METRIC_DEFS],
    [selected],
  );

  // Every column rendered by this table is an allowed sortBy key per the backend contract.
  const isSortable = (key: K) => !!onSortChange;

  const minWidth = useMemo(() => cols.reduce((s, c) => s + (c.width === 'wide' ? WIDE : c.width === 'num' ? NUM : STD), 0), [cols]);

  // Flex-based (not fixed-pixel) so columns stretch to fill the table's full width instead of
  // leaving blank space when the card is wider than the sum of the base widths.
  const widthStyle = (w: C['width']) => (w === 'wide' ? styles.wide : w === 'num' ? styles.num : styles.std);

  return (
    <View style={styles.card}>
      <View style={styles.headerContainer}>
        <View style={styles.top}>
          <Text style={styles.title}>{title}</Text>
          <View style={styles.wrap} ref={columnPickerWrapRef}>
            <TouchableOpacity style={styles.pill} onPress={() => setOpen((p) => !p)}>
              <Text style={styles.pillText}>{`Columns (${selected.length})`}</Text>
            </TouchableOpacity>
            {open && (
              <GroupedColumnPickerPanel
                groups={COLUMN_GROUPS}
                selectedKeys={selected}
                onChange={(keys) => handleColumnsChange(keys as K[])}
              />
            )}
          </View>
        </View>
        {note ? <Text style={styles.note}>{note}</Text> : null}
      </View>

      <CrewingHorizontalScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.scroll}>
        <View style={[styles.table, { minWidth, width: '100%' }]}>
          <View style={styles.headRow}>
            {cols.map((c) => {
              const isActive = sortBy === c.key;
              const sortable = isSortable(c.key);
              return (
                <View key={c.key} style={[styles.headCell, widthStyle(c.width)]}>
                  <TouchableOpacity
                    style={styles.headCellSortable}
                    onPress={sortable ? () => onSortChange!(c.key) : undefined}
                    disabled={!sortable}
                    activeOpacity={sortable ? 0.7 : 1}
                  >
                    <Text style={[styles.headCellText, isActive && styles.headCellTextActive]} numberOfLines={2}>{c.label}</Text>
                    {sortable && (
                      <MaterialIcons
                        name={isActive ? (sortDirection === 'asc' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                        size={14}
                        color={isActive ? '#034895' : '#9CA3AF'}
                      />
                    )}
                  </TouchableOpacity>
                  {c.tooltip ? <View style={styles.headCellInfo}><KpiInfoTooltip text={c.tooltip} /></View> : null}
                </View>
              );
            })}
          </View>

          <View style={styles.total}>
            {cols.map((c, i) => {
              let text = '--';
              if (c.key === 'region') text = 'Total';
              if (c.numeric) {
                const v = backendTotals ? (backendTotals[c.key as keyof QualityMetricsTotals] as number | null) : null;
                text = v == null ? '--' : c.percent ? `${v.toFixed(1)}%` : n(v);
              }
              return <Text key={`${c.key}-t-${i}`} style={[styles.totalCell, widthStyle(c.width)]}>{text}</Text>;
            })}
          </View>

          <View style={styles.tableViewport}>
            <CrewingVerticalScrollView nestedScrollEnabled showsVerticalScrollIndicator>
              {rows.map((r, i) => (
                <View key={`${r.location}-${i}`} style={styles.row}>
                  {cols.map((c) => {
                    const raw = r[c.key] as string | number | null;
                    const isPlaceholderText = (c.key === 'region' || c.key === 'market') && (raw === '0' || raw === 0 || !raw);
                    const text = c.numeric
                      ? (raw == null ? '--' : c.percent ? `${Number(raw).toFixed(1)}%` : n(Number(raw)))
                      : isPlaceholderText
                        ? '--'
                        : String(raw);
                    return <Text key={`${c.key}-${i}`} style={[styles.cell, widthStyle(c.width), c.link ? styles.link : null]}>{text}</Text>;
                  })}
                </View>
              ))}
            </CrewingVerticalScrollView>
          </View>
        </View>
      </CrewingHorizontalScrollView>

      <Text style={styles.footer}>
        {(total ?? rows.length).toLocaleString()} groups total. Percentages are computed from the summed hours in each group (aggregate then divide).
        Accuracy and Efficiency use only the hours from rows with final actuals so both sides of the ratio cover the
        same population; Compliance uses all rows. Blank actuals blank the actual-based rates.
      </Text>
      {onPageChange && (
        <TablePager page={page} pageSize={pageSize} total={total ?? rows.length} onPageChange={onPageChange} loading={pageLoading} pageSizeOptions={pageSizeOptions} onPageSizeChange={onPageSizeChange} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: { backgroundColor: '#FFFFFF', borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB', padding: 14, overflow: 'visible' },
  headerContainer: { flexDirection: 'column', gap: 6, marginBottom: 8, position: 'relative', zIndex: 30 },
  top: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10, position: 'relative', zIndex: 30 },
  title: { fontSize: 16, fontFamily: FontFamily.bold, fontWeight: '700', color: '#1A1D21' },
  rightTop: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  note: { fontSize: 10, fontFamily: FontFamily.regular, color: '#838383' },
  wrap: { position: 'relative', zIndex: 40 },
  pill: { borderWidth: 1, borderColor: '#BFDBFE', backgroundColor: '#EFF6FF', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 },
  pillText: { fontSize: 11, color: '#0B5FC6', fontWeight: '600' },
  scroll: { flexGrow: 1 },
  table: { width: '100%' },
  tableViewport: { flex: 1, minHeight: 260, maxHeight: 420 },
  headRow: { flexDirection: 'row', alignItems: 'stretch', borderBottomWidth: 2, borderBottomColor: '#D7E5F8', backgroundColor: '#FFFFFF', paddingVertical: 8 },
  row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#D7E5F8', paddingVertical: 8 },
  total: { flexDirection: 'row', borderTopWidth: 1, borderTopColor: '#D0D0D0', borderBottomWidth: 2, borderBottomColor: '#D7E5F8', backgroundColor: '#F3F7FD', paddingVertical: 9 },
  headCell: { flexDirection: 'row', alignItems: 'flex-start', gap: 4, paddingHorizontal: 6 },
  headCellSortable: { flex: 1, flexDirection: 'row', alignItems: 'flex-start', gap: 2, minWidth: 0 },
  headCellInfo: { flexShrink: 0, paddingTop: 1 },
  headCellText: { flex: 1, fontSize: 10, fontFamily: FontFamily.bold, color: '#616161', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  headCellTextActive: { color: '#034895' },
  cell: { paddingHorizontal: 6, fontSize: 12, fontFamily: FontFamily.medium, color: '#3A3A3A' },
  totalCell: { paddingHorizontal: 6, fontSize: 12, fontFamily: FontFamily.bold, color: '#2D2D2D', fontWeight: '700' },
  std: { flexGrow: 1, flexBasis: STD, minWidth: STD },
  wide: { flexGrow: 1, flexBasis: WIDE, minWidth: WIDE },
  num: { flexGrow: 1, flexBasis: NUM, minWidth: NUM, textAlign: 'right' },
  link: { color: '#0B5FC6', fontWeight: '600' },
  footer: { marginTop: 10, fontSize: 12, fontFamily: FontFamily.regular, color: '#6B7280', lineHeight: 17 },
});

export default ExecutionQualityMetricsTable;
